import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Snackbar, Alert, Chip, Button } from '@mui/material';
import { Message, MessageType } from '../models/Message';
import { timestampToDate, formatTime } from '../utils/FirestoreUtils';
import { useAuth } from '../context/AuthContext';
import MarkdownText from './MarkdownText';
import PaymentBubble from './PaymentBubble';
import SubmissionReceipt from './SubmissionReceipt';
import PhotoGallery from './PhotoGallery';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { tok } from '../theme/businessTheme';

interface MessageItemProps {
  message: Message;
  showTimestamp?: boolean;
  onShowQuestionnaire?: (message: Message) => void;
  formId?: string;
  channelId?: string;
  /** True when this is the most recent AGENT message in the conversation AND
      the customer hasn't sent anything since — quick-reply chips (Choice
      Questions contract §4) only ever render on that one message. */
  showChoiceChips?: boolean;
  /** Sends the given text as the customer's next message (normal send path)
      and dismisses the chips. */
  onSendChoiceReply?: (text: string) => void;
  /** Opens the customer chat with the agent a transfer card points to. */
  onContinueTransfer?: (toFormId: string) => void;
}

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  showTimestamp = true,
  onShowQuestionnaire,
  formId,
  channelId,
  showChoiceChips = false,
  onSendChoiceReply,
  onContinueTransfer,
}) => {
  const { userId } = useAuth();
  const theme = useMuiTheme();
  const isUserMessage = message.fromId === userId;
  const isSystemMessage = message.fromId === 'system';
  const isIntroMessage = message.id.startsWith('intro_');
  const timestamp = timestampToDate(message.timestamp);
  
  // Log all messages for debugging
  console.log('PAIPER_PAYMENT_LOG: Processing message:', {
    id: message.id,
    messageType: message.messageType,
    messageTypeType: typeof message.messageType,
    hasPaymentData: !!message.paymentData,
    response: message.response,
    fromId: message.fromId,
    messageTextPreview: message.messageText?.substring(0, 50),
    fullMessage: message
  });
  
  // Simple payment detection based on iOS app logic
  const isPaymentMessage = React.useMemo(() => {
    const msgType = message.messageType as string;
    const result = msgType === 'payment' || msgType === 'payment_paid' || msgType === 'payment_refunded';
    
    console.log('PAIPER_PAYMENT_LOG: Message', message.id, 'isPayment:', result, 'messageType:', msgType, 'hasPaymentData:', !!message.paymentData, 'fullMessage:', JSON.stringify(message, null, 2));
    return result;
  }, [message]);

  // Use message as-is since it already has the correct structure
  const enrichedMessage = message;
  
  const [parsedQuestions, setParsedQuestions] = useState<Record<string, Record<string, string>>>({});
  const [showBellConfirmation, setShowBellConfirmation] = useState(false);
  const [bellLoading, setBellLoading] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);

  // Choice quick-reply chips (spec §4) --------------------------------------
  const choicePrompt = message.choicePrompt;
  const [choiceSelected, setChoiceSelected] = useState<string[]>([]);

  const canShowChoiceChips = !!choicePrompt && showChoiceChips && !isUserMessage;

  const toggleChoiceOption = (option: string) => {
    if (!choicePrompt) return;
    if (!choicePrompt.multi) {
      // Single select: tapping a chip immediately sends that option.
      onSendChoiceReply?.(option);
      return;
    }
    setChoiceSelected((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );
  };

  const sendChoiceMulti = () => {
    if (!choicePrompt || choiceSelected.length === 0) return;
    // Join in DEFINITION order (spec §2/§4).
    const ordered = choicePrompt.options.filter((o) => choiceSelected.includes(o));
    onSendChoiceReply?.(ordered.join(', '));
    setChoiceSelected([]);
  };

  // Parse JSON message on mount
  useEffect(() => {
    if ((message.response === 'yes' || message.response === 'submitted') && !isUserMessage) {
      try {
        const jsonObject = JSON.parse(message.messageText);
        const result: Record<string, Record<string, string>> = {};
        
        // Check if the message has Required/Optional sections
        if (jsonObject.Required && jsonObject.Optional) {
          result.Required = {};
          Object.keys(jsonObject.Required).forEach(key => {
            result.Required[key] = jsonObject.Required[key].toString();
          });
          
          result.Optional = {};
          Object.keys(jsonObject.Optional).forEach(key => {
            result.Optional[key] = jsonObject.Optional[key].toString();
          });
        } else {
          // Handle other formats - check if it's a map of sections
          const isPurposeKey = (key: string) => key === 'Purpose';
          const nonPurposeKeys = Object.keys(jsonObject).filter(key => !isPurposeKey(key));
          
          nonPurposeKeys.forEach(section => {
            if (typeof jsonObject[section] === 'object' && jsonObject[section] !== null) {
              result[section] = {};
              Object.keys(jsonObject[section]).forEach(key => {
                result[section][key] = jsonObject[section][key].toString();
              });
            } else {
              // Flat structure - create a Questions section
              if (!result.Questions) {
                result.Questions = {};
              }
              result.Questions[section] = jsonObject[section].toString();
            }
          });
        }
        
        setParsedQuestions(result);
        
        // Auto-notify parent about "yes" response messages
        if (message.response === 'yes' && !isUserMessage && onShowQuestionnaire) {
          onShowQuestionnaire(message);
        }
      } catch (error: any) {
        setParsedQuestions({});
      }
    }
  }, [message, isUserMessage, onShowQuestionnaire]);
  
  // Handler for clicking on a message
  const handleMessageClick = () => {
    if (message.response === 'yes' && !isUserMessage && onShowQuestionnaire) {
      onShowQuestionnaire(message);
    }
  };

  // Handler for bell button click
  const handleBellClick = async (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering message click
    
    if (!formId) {
      console.error('No formId provided for attention request');
      return;
    }

    // Trigger press animation

    setBellLoading(true);
    try {
      await createAttentionRequest(formId, message.id);
      setShowBellConfirmation(true);
    } catch (error) {
      console.error('Failed to request attention:', error);
    } finally {
      setBellLoading(false);
    }
  };

  // Function to create attention request in Firestore
  const createAttentionRequest = async (formId: string, reportId: string) => {
    // Import Firebase functions at the top when implementing
    const { collection, doc, setDoc, getFirestore, serverTimestamp } = await import('firebase/firestore');
    const db = getFirestore();
    
    // Use the channelId passed as prop, with fallbacks
    const actualChannelId = channelId || 
                           localStorage.getItem('channelId') || 
                           localStorage.getItem('currentChannelId') || '';
    
    if (!actualChannelId) {
      throw new Error('Channel ID not found. Cannot create attention request.');
    }
    
    console.log('Creating attention request:', { 
      channelId: actualChannelId, 
      formId, 
      reportId,
      channelIdSource: channelId ? 'prop' : 'localStorage',
      messageId: message.id,
      messageText: message.messageText.substring(0, 100) + '...'
    });
    
    // Use reportId (message.id) as document ID to allow multiple requests per form
    const attentionRef = doc(collection(db, `channels/${actualChannelId}/attention_requests`), reportId);
    
    await setDoc(attentionRef, {
      formId: formId,
      reportId: reportId,
      timestamp: serverTimestamp(),
      customerId: userId // Optional customer identifier
    });
    
    console.log('Attention request created successfully');
  };

  // Chat transfers: agent bubble + "Continue with {agent}" button.
  if (message.response === 'transfer') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', mb: 1 }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: tok('businessBubble'),
            color: tok('onBusinessBubble'),
            border: '1px solid transparent',
            p: 1.5,
            px: 2,
            borderRadius: '18px',
            borderBottomLeftRadius: '6px',
            maxWidth: '80%',
          }}
        >
          <MarkdownText markdown={message.messageText} />
          {message.transferToFormId && (
            <Button
              variant="contained"
              size="small"
              onClick={() => onContinueTransfer?.(message.transferToFormId as string)}
              sx={{
                mt: 1.25,
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 600,
                background: tok('action'),
                color: tok('onAction'),
                '&:hover': { background: tok('action') },
              }}
            >
              Continue with {message.transferToTitle || 'next agent'}
            </Button>
          )}
        </Paper>
        {showTimestamp && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, opacity: 0.7, fontSize: '0.75rem' }}>
            {formatTime(timestamp)}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUserMessage ? 'flex-end' : 'flex-start',
        width: '100%',
        mb: 1,
      }}
    >
      {/* Payment Message Handling */}
      {isPaymentMessage ? (
        <>
          {console.log('PAIPER_PAYMENT_LOG: ✅ RENDERING PAYMENTBUBBLE for message:', enrichedMessage.id)}
          <PaymentBubble 
            message={enrichedMessage}
            formId={formId}
            onPaymentComplete={(msg) => {
              console.log('PAIPER_PAYMENT_LOG: Payment completed for message:', msg.id);
            }}
          />
        </>
      ) : (
        <>
          {console.log('PAIPER_PAYMENT_LOG: ❌ NOT PAYMENT - rendering regular message for:', message.id, 'systemMsg:', isSystemMessage, 'introMsg:', isIntroMessage)}
        </>
      )}
      
      
      {!isPaymentMessage && (isSystemMessage || isIntroMessage) && (
        // System/Intro message styling — a soft sage note card
        <Paper
          elevation={0}
          sx={{
            backgroundColor: tok('card'),
            color: tok('onSurface'),
            p: 1.75,
            borderRadius: '14px',
            maxWidth: '85%',
            border: `1px solid ${tok('cardBorder')}`,
          }}
        >
          <MarkdownText markdown={message.messageText} />
        </Paper>
      )}
      
      {!isPaymentMessage && !isSystemMessage && !isIntroMessage && (message.response === 'yes' || message.response === 'submitted') && !isUserMessage && (
        <Box onClick={handleMessageClick} sx={{ width: '100%', maxWidth: 440, minWidth: 0, cursor: message.response === 'yes' ? 'pointer' : 'default' }}>
          <SubmissionReceipt questions={parsedQuestions} submitted={message.response === 'submitted'}
            busy={bellLoading} onRequestAttention={message.response === 'submitted' && formId ? handleBellClick : undefined} />
        </Box>
      )}
      
      {!isPaymentMessage && !isSystemMessage && !isIntroMessage && !((message.response === 'yes' || message.response === 'submitted') && !isUserMessage) && message.imageMessageUrl && (
        // Image-question photo bubble (spec §4c) — ~200px rounded thumbnail,
        // tap opens the full-screen viewer. Caption is the "📷 Photo attached
        // for: X" text sent alongside it.
        <Box
          sx={{
            maxWidth: '80%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: isUserMessage ? 'flex-end' : 'flex-start',
          }}
        >
          <Box
            onClick={() => setShowImageViewer(true)}
            component="img"
            src={message.imageMessageUrl}
            alt={message.messageText || 'Attached photo'}
            sx={{
              width: 200,
              height: 200,
              objectFit: 'cover',
              borderRadius: '16px',
              borderBottomRightRadius: isUserMessage ? '6px' : '16px',
              borderBottomLeftRadius: isUserMessage ? '16px' : '6px',
              cursor: 'pointer',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.palette.mode === 'light' ? '0 4px 16px rgba(27,58,45,0.12)' : 'none',
            }}
          />
          {message.messageText && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, textAlign: isUserMessage ? 'right' : 'left' }}
            >
              {message.messageText}
            </Typography>
          )}
          <PhotoGallery
            open={showImageViewer}
            urls={[message.imageMessageUrl]}
            onClose={() => setShowImageViewer(false)}
          />
        </Box>
      )}

      {!isPaymentMessage && !isSystemMessage && !isIntroMessage && !((message.response === 'yes' || message.response === 'submitted') && !isUserMessage) && !message.imageMessageUrl && (
        // Regular chat message
        <Paper
          elevation={0}
          sx={{
            ...(isUserMessage
              ? {
                  // The customer's own bubble.
                  backgroundColor: tok('customerBubble'),
                  color: tok('onCustomerBubble'),
                  border: `1px solid ${tok('cardBorder')}`,
                }
              : {
                  backgroundColor: tok('businessBubble'),
                  color: tok('onBusinessBubble'),
                  border: '1px solid transparent',
                }),
            p: 1.5,
            px: 2,
            borderRadius: '18px',
            borderBottomRightRadius: isUserMessage ? '6px' : '18px',
            borderBottomLeftRadius: isUserMessage ? '18px' : '6px',
            maxWidth: '80%',
            cursor: (!isUserMessage && message.response === 'yes') ? 'pointer' : 'default',
          }}
          onClick={handleMessageClick}
        >
          {isUserMessage ? (
            <Typography>{message.messageText}</Typography>
          ) : (
            <MarkdownText markdown={message.messageText} />
          )}
        </Paper>
      )}

      {canShowChoiceChips && choicePrompt && (
        // Quick-reply chips (Choice Questions contract §4) — single tap
        // sends immediately; multi toggles selection with a Send button.
        <Box sx={{ maxWidth: '85%', mt: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {choicePrompt.options.map((option) => {
              const selected = choicePrompt.multi && choiceSelected.includes(option);
              return (
                <Chip
                  key={option}
                  label={option}
                  clickable
                  onClick={() => toggleChoiceOption(option)}
                  variant={selected ? 'filled' : 'outlined'}
                  sx={{
                    borderRadius: '999px',
                    fontWeight: 500,
                    ...(selected
                      ? {
                          background: tok('action'),
                          color: tok('onAction'),
                          border: '1px solid transparent',
                        }
                      : {
                          borderColor: tok('cardBorder'),
                          color: tok('onSurface'),
                        }),
                  }}
                />
              );
            })}
          </Box>
          {choicePrompt.multi && (
            <Button
              variant="contained"
              size="small"
              onClick={sendChoiceMulti}
              disabled={choiceSelected.length === 0}
              sx={{
                mt: 1,
                borderRadius: 999,
                textTransform: 'none',
                background: tok('action'),
                color: tok('onAction'),
                '&:hover': { background: tok('action') },
              }}
            >
              Send
            </Button>
          )}
        </Box>
      )}

      {showTimestamp && (
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            mt: 0.5,
            opacity: 0.7,
            fontSize: '0.75rem',
          }}
        >
          {formatTime(timestamp)}
        </Typography>
      )}
      
      {/* Bell confirmation snackbar */}
      <Snackbar
        open={showBellConfirmation}
        autoHideDuration={3000}
        onClose={() => setShowBellConfirmation(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowBellConfirmation(false)} severity="success">
          Attention request sent!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessageItem;